import PropTypes from 'prop-types';
import React from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import XLsimpleLabel from './XLsimpleLabel';

class XLlabelDisplay extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            sortedLabels: [],
            isLoadingLabels: true,
            infoLoading: true
        };
        this.cancellable1 = null;
    }

    componentDidMount() {
        this.retrieveXlLabel();
    }

    componentWillUnmount() {
        if (this.cancellable1) this.cancellable1.cancel();
    }

    // If an XL label is added or removed it will affect the cardinality. We can use that to update the state which triggers a rerender
    static getDerivedStateFromProps(props, state) {
        if (!state.isErrored && props.values.length !== Object.keys(state.sortedLabels).length) {
            return {
                infoLoading: true
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.infoLoading === true) {
            this.retrieveXlLabel();
        }
    }

    loadData1(data, options, entail = true) {
        GS.handle("get-xl-label-data");
        this.cancellable1 = makeCancelable(GS.loadData(data, options, this.props.graph, entail));
        return this.cancellable1;
    }

    // Load all XL labels here for speed (although concept level should have done it too)
    async retrieveXlLabel() {
        const labelResources = [...this.props.values];
        try {
            GS.handle("get-xl-labels");
            if (labelResources.length > 0) {
                const labelLoadOptions = {
                    predicates: [
                        "http://www.w3.org/2008/05/skos-xl#literalForm",
                        "http://www.w3.org/2004/02/skos/core#inScheme",
                        "https://grafsync.graphifi.com/schema/gs/status"
                    ]
                };
                this.loadData1(labelResources, labelLoadOptions, false)
                    .promise
                    .then(() => {
                        if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                        this.processLabels();
                    })
                    .catch((e) => {
                        if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
                    });
            }
            else {
                if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                this.setState({ isLoadingLabels: false, infoLoading: false, sortedLabels: [] });
            }
        }
        catch (e) {
            if (!this.cancellable1 || (this.cancellable1 && !this.cancellable1.isCancelled())) this.setState({ isErrored: e });
        }
    }

    processLabels() {
        const labels = this.props.values.reduce((obj, iri) => {
            if (GS.isLoaded(iri, this.props.graph)) {
                const data = GS.data(iri, this.props.graph);
                const lang = Object.keys(data.literalForm)[0];
                obj[iri] = {
                    lang,
                    text: data.literalForm[lang]
                };
                return obj;
            }
            else {
                return {};
            }
        }, {});
        let sortedLabels = [...Object.keys(labels)];
        sortedLabels.sort(function (a, b) {
            if (labels[a].lang < labels[b].lang) return -1;
            if (labels[a].lang > labels[b].lang) return 1;
            if (labels[a].text.toLowerCase() < labels[b].text.toLowerCase()) return -1;
            if (labels[a].text.toLowerCase() > labels[b].text.toLowerCase()) return 1;
            return 0;
        });
        this.setState({ isLoadingLabels: false, sortedLabels, infoLoading: false });
    }

    setLabelValue = (iri) => (lang, text) => {
        this.setState(prevState => {
            let labels = Object.assign(prevState.labels);
            labels[iri] = {
                lang,
                text
            };
            return { labels };
        });
    };

    shouldComponentUpdate() {
        return !GS.isMaterialising();
    }

    monitorLinkUpdate = () => this.processLabels();

    render() {

        const { isReadOnly, defaultLang, onConvertXlLabel, onBreakLink, schemeIRI, draggable,
            canBreakIfDefault, showLabelDetail, showFloatingLabel, graph, useWorkflow} = this.props;
        const { sortedLabels, isLoadingLabels, isErrored } = this.state;

        if (isErrored) return <Errored dataTest="xl-error" data={isErrored} />;
        if (!sortedLabels || sortedLabels.length === 0) return null;
        if (isLoadingLabels) return <Loading />;

        return (
            sortedLabels.map((labelIRI) =>
                <XLsimpleLabel
                    draggable={draggable}
                    id={labelIRI}
                    schemeIRI={schemeIRI}
                    graph={graph}
                    useWorkflow={useWorkflow}
                    onBreakLink={onBreakLink}
                    setLabelValue={this.setLabelValue(labelIRI)}
                    onConvertLabel={onConvertXlLabel}
                    canBreakIfDefault={canBreakIfDefault}
                    showFloatingLabel={showFloatingLabel}
                    showLabelDetail={showLabelDetail}
                    updateEvent={this.monitorLinkUpdate}
                    defaultLang={defaultLang}
                    isReadOnly={isReadOnly}
                    key={labelIRI}
                />
            )
        );
    }
}

XLlabelDisplay.defaultProps = {
    isReadOnly: false,
    draggable: false,
    canBreakIfDefault: true,
    useWorkflow: false,
    values: []
};

XLlabelDisplay.propTypes = {
    draggable: PropTypes.bool,
    defaultLang: PropTypes.string,
    schemeIRI: PropTypes.string,
    values: PropTypes.array,
    useWorkflow: PropTypes.bool,
    showFloatingLabel: PropTypes.func.isRequired,
    showLabelDetail: PropTypes.func.isRequired,
    graph: PropTypes.string.isRequired,
    onBreakLink: PropTypes.func.isRequired,
    onConvertXlLabel: PropTypes.func.isRequired,
    canBreakIfDefault: PropTypes.bool,
    isReadOnly: PropTypes.bool
};

export default XLlabelDisplay;